import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import QRCode from "qrcode";
import { useAuth } from "@/context/AuthContext";
import { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Terminal, Loader2, WifiOff, ShieldCheck, ArrowLeft, Smartphone, KeyRound } from "lucide-react";
import { passwordError, PW_HINT } from "@/lib/password";

export default function Login() {
  const { applyAuth } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState("creds"); // creds | pwreset | setup | mfa
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [resetToken, setResetToken] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [secret, setSecret] = useState("");
  const [qr, setQr] = useState("");

  const goToApp = (user) => navigate(user.must_change_password ? "/change-password" : "/");

  const applyStep = async (data) => {
    setError("");
    if (data.status === "password_reset_required") {
      setResetToken(data.reset_token); setNewPw(""); setNewPw2(""); setPhase("pwreset");
    } else if (data.status === "totp_setup") {
      setSetupToken(data.setup_token); setSecret(data.secret);
      setQr(await QRCode.toDataURL(data.otpauth_uri, { margin: 1, width: 200 }));
      setCode(""); setPhase("setup");
    } else if (data.status === "totp_required") {
      setMfaToken(data.mfa_token); setCode(""); setPhase("mfa");
    } else if (data.status === "ok") {
      applyAuth(data.token, data.user); goToApp(data.user);
    }
  };

  const submitCreds = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username: username.trim(), password });
      await applyStep(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setError("");
    const pErr = passwordError(newPw);
    if (pErr) return setError(pErr);
    if (newPw !== newPw2) return setError("Passwords do not match.");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/first-password`, { new_password: newPw }, { headers: { Authorization: `Bearer ${resetToken}` } });
      await applyStep(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const isSetup = phase === "setup";
    const url = isSetup ? `${API}/auth/totp/setup` : `${API}/auth/totp/verify`;
    const token = isSetup ? setupToken : mfaToken;
    try {
      const { data } = await axios.post(url, { code: code.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      applyAuth(data.token, data.user);
      goToApp(data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const backToCreds = () => {
    setPhase("creds"); setError(""); setCode(""); setSecret(""); setQr(""); setNewPw(""); setNewPw2("");
  };

  const HEADING = { creds: "Secure Access", pwreset: "Set New Password", setup: "Set Up 2FA", mfa: "Two-Factor Auth" };
  const inputCls = "font-mono rounded-none bg-background border-border focus-visible:ring-primary";
  const codeCls = "font-mono rounded-none bg-background border-border text-center tracking-[0.5em] text-lg focus-visible:ring-primary";

  return (
    <div className="relative h-screen w-full flex items-center justify-center bg-background text-foreground p-4 scanlines">
      <div className="w-full max-w-sm border border-border bg-surface">
        <div className="border-b border-border px-6 py-6 flex flex-col items-center gap-3 bg-black">
          <img src="/soverenx-logo.png" alt="SoverenX Systems" className="h-14 w-auto object-contain" data-testid="brand-logo" />
          <div className="text-center">
            <h1 className="text-sm font-semibold tracking-tight leading-none">SoverenX Scrubber</h1>
            <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1">{HEADING[phase]}</p>
          </div>
        </div>

        {phase === "creds" && (
          <form onSubmit={submitCreds} className="px-6 py-6 space-y-5">
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground" htmlFor="username">Email</Label>
              <Input id="username" data-testid="login-username-input" autoFocus value={username} onChange={(e) => setUsername(e.target.value)} className={inputCls} placeholder="you@company.com" />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground" htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" />
            </div>
            {error && <p data-testid="login-error" className="text-xs font-mono text-red-400 border border-red-900/60 bg-red-950/30 px-3 py-2">{error}</p>}
            <Button type="submit" data-testid="login-submit-button" disabled={loading} className="w-full rounded-none bg-primary text-primary-foreground hover:bg-accent-hover font-medium tracking-wide">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Terminal className="h-4 w-4 mr-2" /> Continue</>}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center" data-testid="forgot-password-note">
              Forgot your password?{" "}
              <a
                data-testid="admin-contact-link"
                href="mailto:info@soverenxsystems.com.au?subject=SoverenX%20Scrubber%20-%20Password%20reset%20request"
                className="text-primary hover:text-accent-hover underline underline-offset-2"
              >
                Contact your administrator
              </a>{" "}
              to reset it.
            </p>
          </form>
        )}

        {phase === "pwreset" && (
          <form onSubmit={submitNewPassword} className="px-6 py-6 space-y-4" data-testid="pwreset-form">
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <KeyRound className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <span>Set a new password for your account before continuing.</span>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">New Password</Label>
              <Input data-testid="new-password-input" type="password" autoFocus value={newPw} onChange={(e) => setNewPw(e.target.value)} className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Confirm New Password</Label>
              <Input data-testid="confirm-password-input" type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} className={inputCls} />
            </div>
            <p className="text-[10px] text-muted-foreground">{PW_HINT}</p>
            {error && <p data-testid="login-error" className="text-xs font-mono text-red-400 border border-red-900/60 bg-red-950/30 px-3 py-2">{error}</p>}
            <Button type="submit" data-testid="pwreset-submit-button" disabled={loading} className="w-full rounded-none bg-primary text-primary-foreground hover:bg-accent-hover font-medium">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & Continue"}
            </Button>
          </form>
        )}

        {phase === "setup" && (
          <form onSubmit={submitCode} className="px-6 py-6 space-y-4">
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <Smartphone className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <span>Scan with Google or Microsoft Authenticator, then enter the 6-digit code to enable 2FA.</span>
            </div>
            {qr && <div className="flex justify-center py-2"><img src={qr} alt="2FA QR code" data-testid="totp-qr" className="border border-border" /></div>}
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Manual key</p>
              <code data-testid="totp-secret" className="text-xs font-mono break-all text-foreground select-all">{secret}</code>
            </div>
            <Input data-testid="totp-code-input" autoFocus inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className={codeCls} placeholder="000000" />
            {error && <p data-testid="login-error" className="text-xs font-mono text-red-400 border border-red-900/60 bg-red-950/30 px-3 py-2">{error}</p>}
            <Button type="submit" data-testid="totp-submit-button" disabled={loading || code.length !== 6} className="w-full rounded-none bg-primary text-primary-foreground hover:bg-accent-hover font-medium">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-2" /> Enable & Sign In</>}
            </Button>
            <button type="button" onClick={backToCreds} className="w-full text-[11px] text-muted-foreground hover:text-foreground flex items-center justify-center gap-1"><ArrowLeft className="h-3 w-3" /> Back</button>
          </form>
        )}

        {phase === "mfa" && (
          <form onSubmit={submitCode} className="px-6 py-6 space-y-4">
            <p className="text-xs text-muted-foreground text-center">Enter the 6-digit code from your authenticator app.</p>
            <Input data-testid="totp-code-input" autoFocus inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className={codeCls} placeholder="000000" />
            {error && <p data-testid="login-error" className="text-xs font-mono text-red-400 border border-red-900/60 bg-red-950/30 px-3 py-2">{error}</p>}
            <Button type="submit" data-testid="totp-submit-button" disabled={loading || code.length !== 6} className="w-full rounded-none bg-primary text-primary-foreground hover:bg-accent-hover font-medium">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-2" /> Verify & Sign In</>}
            </Button>
            <button type="button" onClick={backToCreds} className="w-full text-[11px] text-muted-foreground hover:text-foreground flex items-center justify-center gap-1"><ArrowLeft className="h-3 w-3" /> Back</button>
          </form>
        )}

        <div className="border-t border-border px-6 py-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <WifiOff className="h-3.5 w-3.5 text-primary" />
          Files are scrubbed locally — never uploaded
        </div>
      </div>

      <p className="absolute bottom-4 left-0 right-0 text-center text-[10px] leading-relaxed text-muted-foreground/70 px-6" data-testid="ip-disclaimer">
        © {new Date().getFullYear()} SoverenX Systems. This tool, its name and logo are the intellectual property of SoverenX Systems. All rights reserved.
      </p>
    </div>
  );
}
